import { Navigate, Outlet, useSearchParams } from "react-router-dom";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import Navigation from "./components/Navigation/Navigation/Navigation";
import urls from "./routes";
import { useLayoutEffect, useState, Suspense } from "react";
import { useDisclosure } from "./ui/util/useDisclosure";
import Modal from "./ui/Modal/Modal";
import ConfirmationModal from "./ui/Modal/ConfirmationModal";
import { AlertTriangle } from "./ui/Icon/Icon";
import Loader from "./ui/Loader/Loader";
import SecondTeamWarningModal from "./components/SecondTeamWarningModal/SecondTeamWarningModal";
import styles from "./Layout.module.scss";

function LoadingState() {
  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Loader />
    </div>
  );
}

function MobileWarningModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal title="Mobile not supported" isOpen={isOpen} onClose={onClose}>
      <div className={styles.mobileWarningBody}>
        <AlertTriangle size="large" className={styles.mobileWarningIcon} />
        <p>
          You can continue to use Macaw on your mobile device, but some features
          may not work as expected.
        </p>
      </div>
      <ConfirmationModal onSubmit={onClose} confirmationText="Continue" />
    </Modal>
  );
}

function Layout() {
  const [searchParams] = useSearchParams();

  const [mobileWarningModalOpened, mobileWarningModal] = useDisclosure();
  const [secondTeamWarningModalOpened, secondTeamWarningModal] = useDisclosure(
    !!searchParams.get("second-team"),
  );
  const [mobileWarningDismissed, setMobileWarningDismissed] = useState(false);

  function handleMobileWarningDismiss() {
    setMobileWarningDismissed(true);
    mobileWarningModal.close();
  }

  useLayoutEffect(() => {
    function handleResize() {
      if (!mobileWarningDismissed && window.innerWidth < 768) {
        mobileWarningModal.open();
      }
    }

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [mobileWarningDismissed, mobileWarningModal]);

  return (
    <>
      <SignedIn>
        <MobileWarningModal
          isOpen={mobileWarningModalOpened}
          onClose={handleMobileWarningDismiss}
        />
        <SecondTeamWarningModal
          isOpen={secondTeamWarningModalOpened}
          onClose={secondTeamWarningModal.close}
        />
        <div className={styles.container}>
          <div className={styles.left}>
            <Navigation />
          </div>
          <div className={styles.right}>
            <Suspense fallback={<LoadingState />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </SignedIn>
      <SignedOut>
        <Navigate replace to={urls.signIn} />
      </SignedOut>
    </>
  );
}

export default Layout;
